/**
 * Utility functions for punishment management
 */

// Check if a punishment is currently active
export const isPunishmentActive = (punishment) => {
    if (!punishment) return false;
    if (punishment.status !== 'active') return false;

    // Check if expired
    if (punishment.expiresAt) {
        const expirationDate = new Date(punishment.expiresAt);
        const now = new Date();
        if (expirationDate < now) {
            return false;
        }
    }

    return true;
};

// Check if punishment can be appealed
export const canAppeal = (punishment) => {
    if (!punishment) return false;
    if (punishment.status !== 'active') return false;
    if (punishment.appealTicketId) return false; // Already appealed
    return true;
};

// Format punishment duration for display
export const formatPunishmentDuration = (punishment) => {
    if (!punishment.expiresAt) {
        return 'Indeterminado';
    }

    const expirationDate = new Date(punishment.expiresAt);
    return expirationDate.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

// Get color scheme based on punishment type
export const getPunishmentSeverityColor = (type) => {
    const colors = {
        'Advertência': {
            bg: 'bg-yellow-500/10',
            border: 'border-yellow-500/30',
            text: 'text-yellow-400',
            gradient: 'from-yellow-600 to-orange-600',
            glow: 'shadow-yellow-500/20'
        },
        'Suspensão Temporária': {
            bg: 'bg-orange-500/10',
            border: 'border-orange-500/30',
            text: 'text-orange-400',
            gradient: 'from-orange-600 to-red-600',
            glow: 'shadow-orange-500/20'
        },
        'Banimento Permanente': {
            bg: 'bg-red-500/10',
            border: 'border-red-500/30',
            text: 'text-red-400',
            gradient: 'from-red-600 to-rose-600',
            glow: 'shadow-red-500/20'
        },
        'Remoção de Whitelist': {
            bg: 'bg-purple-500/10',
            border: 'border-purple-500/30',
            text: 'text-purple-400',
            gradient: 'from-purple-600 to-pink-600',
            glow: 'shadow-purple-500/20'
        },
        'Restrição de Personagem': {
            bg: 'bg-blue-500/10',
            border: 'border-blue-500/30',
            text: 'text-blue-400',
            gradient: 'from-blue-600 to-cyan-600',
            glow: 'shadow-blue-500/20'
        },
        'Amarração': {
            bg: 'bg-yellow-500/10',
            border: 'border-yellow-500/30',
            text: 'text-yellow-400',
            gradient: 'from-yellow-600 to-orange-600',
            glow: 'shadow-yellow-500/20'
        },
        'Blacklist de Tickets': {
            bg: 'bg-red-500/10',
            border: 'border-red-500/30',
            text: 'text-red-400',
            gradient: 'from-red-600 to-rose-600',
            glow: 'shadow-red-500/20'
        }
    };

    return colors[type] || {
        bg: 'bg-gray-500/10',
        border: 'border-gray-500/30',
        text: 'text-gray-400',
        gradient: 'from-gray-600 to-gray-600',
        glow: 'shadow-gray-500/20'
    };
};

// Get status color scheme
export const getStatusColor = (status) => {
    const colors = {
        'active': {
            bg: 'bg-red-500/10',
            border: 'border-red-500/30',
            text: 'text-red-400',
            badge: 'bg-red-500/20 text-red-300 border-red-500/30'
        },
        'under_review': {
            bg: 'bg-orange-500/10',
            border: 'border-orange-500/30',
            text: 'text-orange-400',
            badge: 'bg-orange-500/20 text-orange-300 border-orange-500/30'
        },
        'revoked': {
            bg: 'bg-green-500/10',
            border: 'border-green-500/30',
            text: 'text-green-400',
            badge: 'bg-green-500/20 text-green-300 border-green-500/30'
        },
        'expired': {
            bg: 'bg-gray-500/10',
            border: 'border-gray-500/30',
            text: 'text-gray-400',
            badge: 'bg-gray-500/20 text-gray-300 border-gray-500/30'
        }
    };

    return colors[status] || colors['active'];
};

// Format status text for display
export const formatStatusText = (status) => {
    const statusTexts = {
        'active': 'Ativa',
        'under_review': 'Em Revisão',
        'revoked': 'Revogada',
        'expired': 'Expirada'
    };

    return statusTexts[status] || status;
};

// Calculate days until expiration
export const getDaysUntilExpiration = (punishment) => {
    if (!punishment.expiresAt) return null;

    const expirationDate = new Date(punishment.expiresAt);
    const now = new Date();
    const diffTime = expirationDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > 0 ? diffDays : 0;
};

// Format time remaining
export const formatTimeRemaining = (punishment) => {
    if (!punishment.expiresAt) return 'Indeterminado';

    const days = getDaysUntilExpiration(punishment);

    if (days === null) return 'Indeterminado';
    if (days === 0) return 'Expira hoje';
    if (days === 1) return '1 dia restante';
    if (days < 30) return `${days} dias restantes`;

    const months = Math.floor(days / 30);
    if (months === 1) return '1 mês restante';
    return `${months} meses restantes`;
};

// Punishment type options for forms
export const PUNISHMENT_TYPES = [
    'Advertência',
    'Suspensão Temporária',
    'Banimento Permanente',
    'Remoção de Whitelist',
    'Restrição de Personagem',
    'Amarração',
    'Blacklist de Tickets'
];

// Generate unique punishment ID
export const generatePunishmentId = () => {
    return `PUN-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Calculate expiration date based on duration and type
 * @param {string|number} duration 
 * @param {string} type 'days' | 'hours'
 * @param {string} punishmentType 
 * @param {boolean} isIndefinite 
 * @returns {string|null} ISO string or null for indefinite/permanent
 */
export const calculateExpiration = (duration, type, punishmentType, isIndefinite) => {
    if (isIndefinite || punishmentType === 'Banimento Permanente' || duration === 'indefinido') return null;
    if (!duration) return null;

    const date = new Date();
    const value = parseInt(duration);

    if (isNaN(value)) return null;

    if (type === 'days') date.setDate(date.getDate() + value);
    else if (type === 'hours') date.setHours(date.getHours() + value);
    else date.setDate(date.getDate() + value); // Default to days

    return date.toISOString();
};
